import {
  BrowserCacheLocation,
  IPublicClientApplication,
  InteractionType,
  NavigationClient,
  NavigationOptions,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  MsalGuardConfiguration,
  MsalInterceptorAuthRequest,
  MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { HttpRequest } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { environment } from 'environments/environment';

function tenantFromAuthority(authority: string): string {
  try {
    const host = new URL(authority).hostname;
    return host.split('.')[0] || 'postalbridge';
  } catch {
    return 'postalbridge';
  }
}

function absoluteFrom(base: string): string {
  const trimmed = String(base ?? '').replace(/\/+$/, '');
  if (/^[a-z][a-z0-9+.+-]*:\/\//i.test(trimmed)) return trimmed;
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  let origin = '';
  try {
    origin = window.location.origin;
  } catch {
    origin = new URL(environment.appUrl).origin;
  }

  return `${origin}${path}`;
}

function resolveRedirects() {
  const auth = environment.msalConfig.auth as any;

  const platform =
    typeof Capacitor !== 'undefined' && typeof Capacitor.getPlatform === 'function'
      ? Capacitor.getPlatform()
      : 'web';

  const isNative = platform === 'android' || platform === 'ios';

  let rawRedirect: string | undefined;

  if (isNative) {
    // On native, prefer explicit native URI, then mobile, then derived base, then fallback to web
    rawRedirect =
      auth.nativeRedirectUri ??
      auth.mobileRedirectUri ??
      (auth.mobileRedirectBase
        ? `${String(auth.mobileRedirectBase).replace(/\/+$/, '')}/auth-redirect`
        : auth.redirectUri);
  } else {
    rawRedirect = auth.redirectUri;
  }

  const rawPostLogout = auth.postLogoutRedirectUri ?? environment.appUrl;

  return {
    redirectUri: absoluteFrom(rawRedirect || '/auth-redirect'),
    postLogoutRedirectUri: absoluteFrom(rawPostLogout || '/'),
  };
}

function getPlatformInfo() {
  const platform =
    typeof Capacitor !== 'undefined' && typeof Capacitor.getPlatform === 'function'
      ? Capacitor.getPlatform()
      : 'web';

  return {
    platform,
    isNative: platform === 'android' || platform === 'ios',
  };
}

class CapacitorBrowserNavigationClient extends NavigationClient {
  override async navigateInternal(url: string, options: NavigationOptions): Promise<boolean> {
    // Treat internal navigations as external on native.
    return this.navigateExternal(url, options);
  }

  override async navigateExternal(url: string, _options: NavigationOptions): Promise<boolean> {
    try {
      await Browser.open({ url });
      return true;
    } catch {
      try {
        window.location.assign(url);
        return true;
      } catch {
        return false;
      }
    }
  }
}

export function MSALInstanceFactory(): IPublicClientApplication {
  const { auth } = environment.msalConfig as any;
  const { redirectUri, postLogoutRedirectUri } = resolveRedirects();

  const { isNative } = getPlatformInfo();

  const pca = new PublicClientApplication({
    auth: {
      clientId: auth.clientId,
      authority: auth.authority,
      knownAuthorities: auth.knownAuthorities,
      redirectUri,
      postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
  });

  if (isNative) {
    pca.setNavigationClient(new CapacitorBrowserNavigationClient());
  }

  return pca;
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: environment.apiConfig.scopes },
    loginFailedRoute: '/auth-redirect',
  };
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const tenant = tenantFromAuthority(environment.msalConfig.auth.authority);
  const scopes = [...environment.apiConfig.scopes];

  const apiRel = String(environment.apiConfig.apiUrl || '/api').replace(/\/+$/, '');
  const odRel = String((environment.apiConfig as any).odataUrl || '/odata').replace(/\/+$/, '');

  const apiAbs = absoluteFrom(apiRel);
  const odAbs = absoluteFrom(odRel);

  const map = new Map<string, string[]>();
  const add = (base: string) => {
    const v = base.replace(/\/+$/, '');
    map.set(v, scopes);
    map.set(`${v}/`, scopes);
  };

  // protect relative + absolute
  add(apiRel);
  add(odRel);
  add(apiRel.replace(/^\//, ''));
  add(odRel.replace(/^\//, ''));
  add(apiAbs);
  add(odAbs);

  const protectedPrefixes = [...map.keys()];
  const isProtected = (url: string) => protectedPrefixes.some(p => url.startsWith(p));

  const authRequest = (
    msal: MsalService,
    req: HttpRequest<unknown>,
    original: MsalInterceptorAuthRequest,
  ): MsalInterceptorAuthRequest | null => {
    if (!isProtected(req.url)) return null;

    const account = msal.instance.getActiveAccount() || msal.instance.getAllAccounts()[0];
    const tfp =
      (account?.idTokenClaims as any)?.tfp ??
      (account?.idTokenClaims as any)?.acr ??
      localStorage.getItem('LastUsedB2CPolicy');

    const authority = tfp
      ? `https://${tenant}.b2clogin.com/${tenant}.onmicrosoft.com/${tfp}`
      : undefined;

    return {
      ...original,
      account: account ?? original.account,
      authority: authority ?? original.authority,
    };
  };

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: map,
    authRequest,
  };
}
